import { useEffect, useState } from "react";
import { ScanLine, FilePlus2, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { CardCaptureDialog } from "@/components/CardCaptureDialog";
import CustomerImportanceBadge from "@/components/CustomerImportanceBadge";

const EMPTY = {
  name: "", seller: "", email: "", contact_person: "", phone: "", title: "", website: "",
  address: "", destination_port: "", payment_terms: "", currency: "USD",
  price_multiplier: 1, shipping_fee: 0,
  importance_level: 0, customer_type: "", importance_reason: "", assessment_report: "",
};

/**
 * Compact 新增/编辑客户 dialog used from the quote editor so the user never has
 * to leave the 报价单 page. Writes straight to /customers (same endpoints as
 * the 客户管理 page) and hands the saved customer back to the caller.
 */
export default function CustomerQuickDialog({ open, customer, onClose, onSaved, onSavedAndCreateQuote }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isExport = user?.role === "export";
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // 2026-10-26 · 保存并立即新建报价单 —— 展会上拍完名片，一个按钮就把客户建好并
  //  落到一张空报价单上，不用先回客户管理页再回报价单页。
  const [savingAndCreate, setSavingAndCreate] = useState(false);
  // 2026-10-25 · 拍照名片 OCR 自动回填
  const [cardOpen, setCardOpen] = useState(false);
  // 2026-10-27 · 评估报告折叠状态（默认收起）
  const [reportOpen, setReportOpen] = useState(false);
  const applyCard = (card) => {
    // 2026-10-25 · 仅覆盖"识别到了且当前为空"的字段，避免打断用户已经敲好的内容
    // 2026-10-27 · 名片 OCR 现在同时返回 AI 评级 + 评估报告；评级字段直接覆盖
    //  (per spec：仅 AI 自动打分，不可修改)
    setForm((f) => ({
      ...f,
      name: card.name || f.name,
      contact_person: card.contact_person || f.contact_person,
      email: card.email || f.email,
      phone: card.phone || f.phone,
      title: card.title || f.title,
      website: card.website || f.website,
      address: card.address || f.address,
      // AI 画像字段 - 覆盖式写入，不与现有值合并
      importance_level: card.importance_level ?? f.importance_level ?? 0,
      customer_type: card.customer_type || f.customer_type || "",
      importance_reason: card.importance_reason || f.importance_reason || "",
      assessment_report: card.assessment_report || f.assessment_report || "",
    }));
    // 名片识别后，默认展开评估报告给用户看
    if (card.assessment_report) setReportOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    setForm(customer ? { ...customer } : { ...EMPTY, seller: isExport ? user?.name || "" : "" });
  }, [open, customer, isExport, user?.name]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error(t("customers.name"));
      return;
    }
    setSaving(true);
    try {
      const { data } = form.id
        ? await api.put(`/customers/${form.id}`, form)
        : await api.post("/customers", form);
      toast.success(t("common.save"));
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // 2026-10-26 · 保存客户后立即新建一张以此为客户的报价单草稿。按钮只在
  //  `onSavedAndCreateQuote` prop 存在且这是"新建客户"时才渲染，避免编辑场景
  //  意外开出新报价。
  const handleSaveAndCreateQuote = async () => {
    if (!form.name?.trim()) {
      toast.error(t("customers.name"));
      return;
    }
    setSavingAndCreate(true);
    try {
      const { data } = form.id
        ? await api.put(`/customers/${form.id}`, form)
        : await api.post("/customers", form);
      await onSavedAndCreateQuote?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingAndCreate(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="quote-customer-quick-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center justify-between gap-2">
            <span>{form.id ? t("customers.editTitle") : t("customers.newTitle")}</span>
            {/* 2026-10-25 · 名片 OCR —— 对准相机取景框一拍，自动回填客户信息 */}
            {!form.id && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setCardOpen(true)}
                className="h-8 rounded-md border-brand/40 text-brand hover:bg-brand/5"
                data-testid="quick-customer-scan-card-button"
              >
                <ScanLine size={13} className="mr-1" /> {t("customers.scanCard") || "拍名片自动填"}
              </Button>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("customers.title")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* 2026-10-27 · AI 客户画像 —— 名片扫描后自动填充；per 规则 仅 AI 可写不可改 */}
          {(form.importance_level > 0 || form.importance_reason || form.assessment_report) && (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 space-y-2"
              data-testid="customer-ai-importance-panel"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <Sparkles size={14} className="text-amber-500" />
                  <span className="text-xs font-semibold text-amber-900">
                    {t("customers.importance") || "客户重要性"}
                  </span>
                </div>
                <CustomerImportanceBadge
                  level={form.importance_level}
                  customerType={form.customer_type}
                />
              </div>
              {form.importance_reason && (
                <p
                  className="text-[11px] leading-snug text-amber-900/90"
                  data-testid="customer-ai-importance-reason"
                >
                  {form.importance_reason}
                </p>
              )}
              {form.assessment_report && (
                <div className="pt-1 border-t border-amber-200/70">
                  <button
                    type="button"
                    onClick={() => setReportOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-1 text-[11px] font-medium text-amber-900 hover:text-amber-700"
                    data-testid="customer-ai-report-toggle"
                  >
                    <span>
                      {reportOpen
                        ? (t("customers.hideReport") || "收起")
                        : (t("customers.showReport") || "查看完整评估报告")}
                    </span>
                    {reportOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  </button>
                  {reportOpen && (
                    <div
                      className="mt-1.5 text-[11px] leading-relaxed text-slate-800 whitespace-pre-wrap break-words"
                      data-testid="customer-ai-assessment-report"
                      // Simple bold rendering for **Title**： patterns the LLM is instructed to use
                      dangerouslySetInnerHTML={{
                        __html: (form.assessment_report || "")
                          .replace(/&/g, "&amp;")
                          .replace(/</g, "&lt;")
                          .replace(/>/g, "&gt;")
                          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-amber-900">$1</strong>'),
                      }}
                    />
                  )}
                </div>
              )}
              <p className="text-[10px] text-amber-700/70 italic">
                {t("customers.importanceAiOnly") || "由 AI 根据名片智能评定，不可手动修改"}
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("customers.name")} *</Label>
              <Input value={form.name || ""} onChange={(e) => setField("name", e.target.value)}
                data-testid="quick-customer-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.seller")}</Label>
              <Input value={form.seller || ""} disabled={isExport}
                onChange={(e) => setField("seller", e.target.value)}
                data-testid="quick-customer-seller-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("customers.email")}</Label>
              <Input type="email" value={form.email || ""} onChange={(e) => setField("email", e.target.value)}
                data-testid="quick-customer-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.contactPerson")}</Label>
              <Input value={form.contact_person || ""} onChange={(e) => setField("contact_person", e.target.value)}
                data-testid="quick-customer-contact-input" />
            </div>
          </div>
          {/* 2026-10-27 · 联系电话 + 职位 + 网址 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("customers.phone") || "联系电话"}</Label>
              <Input value={form.phone || ""} onChange={(e) => setField("phone", e.target.value)}
                data-testid="quick-customer-phone-input"
                placeholder="+1 212-555-0100" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.title_label") || "职位"}</Label>
              <Input value={form.title || ""} onChange={(e) => setField("title", e.target.value)}
                data-testid="quick-customer-title-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("customers.website") || "公司网址"}</Label>
            <Input value={form.website || ""} onChange={(e) => setField("website", e.target.value)}
              data-testid="quick-customer-website-input"
              placeholder="example.com" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("customers.address")}</Label>
            <Textarea rows={2} value={form.address || ""} onChange={(e) => setField("address", e.target.value)}
              data-testid="quick-customer-address-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("customers.destinationPort")}</Label>
              <Input value={form.destination_port || ""} onChange={(e) => setField("destination_port", e.target.value)}
                data-testid="quick-customer-port-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.shippingFee")}</Label>
              <Input type="number" step="0.01" min="0" placeholder="0"
                value={form.shipping_fee ?? 0}
                onChange={(e) => setField("shipping_fee", parseFloat(e.target.value) || 0)}
                data-testid="quick-customer-shipping-fee-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("customers.paymentTerms")}</Label>
            <Textarea rows={2} value={form.payment_terms || ""} onChange={(e) => setField("payment_terms", e.target.value)}
              data-testid="quick-customer-payment-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>{t("customers.currency")}</Label>
              <Select value={form.currency || "USD"} onValueChange={(v) => setField("currency", v)}>
                <SelectTrigger data-testid="quick-customer-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD 美金</SelectItem>
                  <SelectItem value="CAD">CAD 加币</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t("customers.priceMultiplier")}</Label>
              <Input type="number" step="0.01" min="0" placeholder="1.0"
                value={form.price_multiplier ?? 1}
                onChange={(e) => setField("price_multiplier", parseFloat(e.target.value) || 0)}
                data-testid="quick-customer-price-multiplier-input" />
              <p className="text-[11px] text-muted-foreground">{t("customers.priceMultiplierHint")}</p>
            </div>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onClose?.()} className="rounded-md" disabled={saving || savingAndCreate}>{t("common.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving || savingAndCreate}
            data-testid="quick-customer-save-button"
            className="rounded-md bg-brand hover:bg-brand-hover">
            {saving ? "…" : t("common.save")}
          </Button>
          {/* 2026-10-26 · 一键"保存名片 + 立即开报价单草稿"
              只在新建客户 + 外层传了 onSavedAndCreateQuote 时才出现 */}
          {!form.id && onSavedAndCreateQuote && (
            <Button
              onClick={handleSaveAndCreateQuote}
              disabled={saving || savingAndCreate}
              data-testid="quick-customer-save-and-quote-button"
              className="rounded-md bg-amber-500 hover:bg-amber-600 text-white"
            >
              <FilePlus2 size={14} className="mr-1" />
              {savingAndCreate ? "…" : (t("customers.saveAndNewQuote") || "保存并新建报价")}
            </Button>
          )}
        </DialogFooter>
        {/* 2026-10-25 · 名片 OCR 子对话框 —— 识别成功后 applyCard 把字段回填到当前表单 */}
        <CardCaptureDialog
          open={cardOpen}
          onOpenChange={setCardOpen}
          onDetect={applyCard}
        />
      </DialogContent>
    </Dialog>
  );
}
